import {
  batchCreateBasicField,
  batchDeleteElementAndSubs,
  createFieldValue,
  getNestedOrFloatingElementById,
  INDEXED_INCORRECTLY,
} from '@fs/zion-sls'
import { FullTextApp } from '../../../../../shared/services/config'
import { compareLinkedPersons, getSharedElement } from '../../panels/linkNamesPanel/utils/linkedPersonsHelpers'
import {
  createLinkPersonDispatches,
  createUnlinkPersonDispatches,
  createUnlinkDispatch,
} from '../../panels/linkNamesPanel/utils/linkingDispatchHelpers'

function handleExistingField(defaultValues, fact, existingField, attr) {
  const defaultValue = defaultValues?.additionalFacts.find((defaultVal) => defaultVal.id === fact.id)
  if (fact.value !== defaultValue?.value) {
    const fieldValue = createFieldValue(fact.value, { ...attr, reasonId: INDEXED_INCORRECTLY })
    return [
      {
        type: 'ADD_FV',
        value: {
          id: fact.id,
          partitionKey: existingField.partitionKey,
          fieldValue,
        },
      },
    ]
  }
  return []
}

function handleNewField(person, fact, attr) {
  const existingDispatches = fact?.dispatches || []
  const existingFieldValueDispatch = existingDispatches.find((d) => d.type === 'ADD_FV' && d.value.id === fact.id)

  // If the field value was previously created (e.g by the shared highlight flow), reuse the dispatches for this fact
  // and add a dispatch to link it to the person
  if (existingFieldValueDispatch) {
    const addDispatch = {
      type: 'ADD_SUB',
      value: {
        superPartitionKey: person.partitionKey,
        subPartitionKey: person.partitionKey,
        superId: person.id,
        subId: fact.id,
        elementType: fact.key,
      },
    }

    return [...existingDispatches, addDispatch]
  }

  const fieldValue = createFieldValue(fact.value, { ...attr, reasonId: INDEXED_INCORRECTLY })

  return batchCreateBasicField({
    partitionKey: person.partitionKey,
    superId: person.id,
    id: fact.id,
    fieldType: fact.key,
    superElementType: 'PERSON',
    fieldValue,
  })
}

/**
 * Generates dispatch actions for editing, adding, and removing additional facts.
 * @param {Array} additionalFacts - the values of the form associated with additional facts
 * @param {Object} defaultValues - values the form was initialized with
 * @param {Object} person - the SLS person object being edited
 * @param {Array} images - the SLS images array
 * @returns {Array} Array of dispatch actions to be processed
 */
export default function getAdditionalFactDispatches({ additionalFacts, defaultValues, person, images }) {
  let dispatches = []
  const attr = { comment: '', reasonId: '', app: FullTextApp } // Eric said to use an empty reason id when creating new fields

  additionalFacts.forEach((fact) => {
    const existingField = getNestedOrFloatingElementById(images, fact.id)
    if (existingField) {
      dispatches = dispatches.concat(handleExistingField(defaultValues, fact, existingField, attr))
    } else {
      dispatches = dispatches.concat(handleNewField(person, fact, attr))
    }

    // Handle links/unlinks
    const orig = defaultValues.additionalFacts.find((f) => f.id === fact.id) || {}
    const { addedPersons, removedPersons } = compareLinkedPersons(
      orig.linkedPersonIds || [],
      fact.linkedPersonIds || []
    )

    addedPersons.forEach((p) => {
      const addedPerson = getNestedOrFloatingElementById(images, p.id)
      const sharedElement = getSharedElement(images, fact.id)

      const linkDispatches = createLinkPersonDispatches({
        person: addedPerson,
        sharedElement,
        images,
      })

      dispatches.push(...linkDispatches)
    })

    removedPersons.forEach((p) => {
      const removedPerson = getNestedOrFloatingElementById(images, p.id)
      const factElement = getNestedOrFloatingElementById(images, fact.id)

      const unlinkDispatches = createUnlinkPersonDispatches({
        person: removedPerson,
        fieldId: factElement.id,
        fieldKey: fact.key,
        partitionKey: factElement.partitionKey,
        images,
      })

      dispatches.push(...unlinkDispatches)
    })
  })

  defaultValues.additionalFacts.forEach((fact) => {
    if (!additionalFacts.find((af) => af.id === fact.id)) {
      const existingField = getNestedOrFloatingElementById(images, fact.id)
      if (existingField) {
        if (existingField.superElements?.length > 1) {
          // Field is shared, so just unlink it from this person
          dispatches.push(
            createUnlinkDispatch({
              person,
              element: existingField,
            })
          )
        } else {
          // Field was deleted in the form, so delete it in SLS
          dispatches = dispatches.concat(batchDeleteElementAndSubs({ element: existingField }))
        }
      }
    }
  })

  return dispatches
}
